import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Flex } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { Button, DefaultIconButton, Heading, Popover, Radio, Text } from '../Ant';
import { Close, SortAscending, SortDescending } from '../icons';
import { usePopupFocus } from '../../hooks/usePopupFocus';
import {
  SortDirectionOptions,
  SortOption,
  SortPopoverFooter,
  SortRadioGroup,
} from './sortPopover.styled';
import {
  IActiveSortState,
  ISortOption,
  SortDirection,
  SortFieldType,
} from '../../interface/common';

export function getDirectionLabels(type: SortFieldType, t: TFunction) {
  switch (type) {
    case 'number':
      return { asc: t('common:sorting.oneToNine'), desc: t('common:sorting.nineToOne') };
    case 'date':
      return { asc: t('common:sorting.oldestFirst'), desc: t('common:sorting.newestFirst') };
    default:
      return { asc: t('common:sorting.aToZ'), desc: t('common:sorting.zToA') };
  }
}

export function getOrderForLabel(translatedOrderForLabel: string, optionLabel: string) {
  const normalizedOptionLabel = optionLabel.toLocaleLowerCase();
  const trimmedOrderForLabel = translatedOrderForLabel.trim();

  if (!normalizedOptionLabel) {
    return trimmedOrderForLabel;
  }

  if (!trimmedOrderForLabel || trimmedOrderForLabel === 'common:sorting.orderFor') {
    return `Order for ${normalizedOptionLabel}`;
  }

  const interpolatedOrderForLabel = trimmedOrderForLabel
    .replace(/\{\{\s*label\s*\}\}/g, normalizedOptionLabel)
    .trim();

  if (interpolatedOrderForLabel.toLocaleLowerCase().includes(normalizedOptionLabel)) {
    return interpolatedOrderForLabel;
  }

  return `${interpolatedOrderForLabel} ${normalizedOptionLabel}`;
}

interface SortPopoverContentProperties {
  popoverTitle: string;
  currentSortLabel: string;
  options: ISortOption[];
  selectedField: string;
  selectedDirection: SortDirection;
  onFieldChange: (e: RadioChangeEvent) => void;
  onDirectionChange: (field: string, direction: SortDirection) => void;
  onClose: () => void;
  onClear?: () => void;
  onApply?: () => void;
  focus: ReturnType<typeof usePopupFocus>;
}

const SortPopoverContent = ({
  popoverTitle,
  currentSortLabel,
  options,
  selectedField,
  selectedDirection,
  onFieldChange,
  onDirectionChange,
  onClose,
  onClear,
  onApply,
  focus,
}: SortPopoverContentProperties) => {
  const { t } = useTranslation();
  const selectedOption = options.find((option) => option.field === selectedField) ?? options[0];
  const directionLabels = getDirectionLabels(selectedOption.type, t);
  const selectedOptionLabel = selectedOption.label.toLocaleLowerCase();
  const orderForLabel = getOrderForLabel(
    t('common:sorting.orderFor', {
      defaultValue: 'Order for {{label}}',
      label: selectedOptionLabel,
    }),
    selectedOption.label,
  );

  return (
    <Flex
      vertical
      gap={20}
      data-testid="sort-popover-content"
      role="dialog"
      aria-label={popoverTitle}
      ref={focus.contentRef}
      tabIndex={focus.tabIndex}
      onKeyDown={focus.onKeyDown}
      onBlur={focus.onBlur}
    >
      <Flex align="flex-start" justify="space-between" gap={10}>
        <Flex vertical className="min-w-0">
          <Heading
            data-testid="sort-popover-title"
            level={3}
            $color="title"
            $fontsize="14px"
            $weight={600}
            $lineheight="21px"
          >
            {popoverTitle}
          </Heading>
          <Text
            data-testid="sort-popover-current"
            $fontsize="12px"
            $color="description"
            $lineheight="16px"
            $weight={500}
          >
            {t('common:sorting.currently')} : {currentSortLabel}
          </Text>
        </Flex>
        <DefaultIconButton
          data-testid="sort-close-button"
          onClick={onClose}
          type="text"
          icon={
            <span aria-hidden="true">
              <Close className="fz-12" />
            </span>
          }
          aria-label={t('common:bottomNavigation.close')}
        />
      </Flex>
      <SortRadioGroup
        value={selectedField}
        onChange={onFieldChange}
        aria-label={popoverTitle}
        data-testid="sort-radio-group"
      >
        {options.map((option) => {
          const isSelected = selectedField === option.field;
          const labels = getDirectionLabels(option.type, t);
          const directionLabel = selectedDirection === 'ASC' ? labels.asc : labels.desc;
          return (
            <SortOption
              key={option.field}
              className={isSelected ? 'selected' : ''}
              data-testid={`sort-option-${option.field}`}
            >
              <Radio
                value={option.field}
                data-testid={`sort-radio-${option.field}`}
                className="gap-10"
              >
                <Flex align="center" justify="space-between" gap={10} className="sort-option-row">
                  <Text
                    $fontsize="13px"
                    $color={isSelected ? 'primary' : 'title'}
                    $weight={500}
                    $lineheight="18px"
                    $width="auto"
                    className="flex-shrink-0"
                  >
                    {option.label}
                  </Text>
                  {isSelected && (
                    <Text
                      $fontsize="12px"
                      $color="subtitle"
                      $weight={600}
                      $lineheight="18px"
                      data-testid={`sort-option-direction-${option.field}`}
                      className="ta-right flex-shrink-0"
                      $width="auto"
                    >
                      {directionLabel}
                    </Text>
                  )}
                </Flex>
              </Radio>
            </SortOption>
          );
        })}
      </SortRadioGroup>
      <Flex vertical gap={5} data-testid="sort-direction-section">
        <Text
          data-testid="sort-direction-label"
          $fontsize="12px"
          $color="description"
          $lineheight="16px"
          $weight={500}
        >
          {orderForLabel}
        </Text>
        <SortDirectionOptions gap={5} data-testid="sort-direction-options">
          <Button
            className={selectedDirection === 'ASC' ? 'selected' : ''}
            aria-pressed={selectedDirection === 'ASC'}
            icon={<SortDescending />}
            onClick={() => onDirectionChange(selectedField, 'ASC')}
            data-testid="sort-direction-asc"
          >
            {directionLabels.asc}
          </Button>
          <Button
            className={selectedDirection === 'DESC' ? 'selected' : ''}
            aria-pressed={selectedDirection === 'DESC'}
            icon={<SortAscending />}
            onClick={() => onDirectionChange(selectedField, 'DESC')}
            data-testid="sort-direction-desc"
          >
            {directionLabels.desc}
          </Button>
        </SortDirectionOptions>
      </Flex>
      <SortPopoverFooter gap={5} data-testid="sort-popover-footer">
        {onClear && (
          <Button data-testid="sort-clear-button" onClick={onClear} maxwidth="100px">
            {t('common:timers.reset')}
          </Button>
        )}
        {onApply && (
          <Button type="primary" data-testid="sort-apply-button" onClick={onApply}>
            {t('common:apply')}
          </Button>
        )}
      </SortPopoverFooter>
    </Flex>
  );
};

export interface ISortPopover {
  options: ISortOption[];
  activeSort: IActiveSortState;
  onApply?: (sort: IActiveSortState) => void;
  onCloseModal?: () => void;
  modalLabel?: string;
  visible: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

const SortPopover = ({
  options,
  activeSort,
  onApply,
  onCloseModal,
  modalLabel,
  visible,
  onOpenChange,
  children,
}: ISortPopover) => {
  const { t } = useTranslation();
  const [selectedField, setSelectedField] = useState<string>(activeSort.field);
  const [selectedDirection, setSelectedDirection] = useState<SortDirection>(activeSort.direction);

  useEffect(() => {
    if (visible) {
      setSelectedField(activeSort.field);
      setSelectedDirection(activeSort.direction);
    }
  }, [visible, activeSort]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      onCloseModal?.();
    }
  };

  const dismiss = () => handleOpenChange(false);
  const popupFocus = usePopupFocus(visible, dismiss);

  const handleFieldChange = (e: RadioChangeEvent) => {
    const field = e.target.value as string;
    if (field !== selectedField) {
      const option = options.find((o) => o.field === field);
      setSelectedField(field);
      setSelectedDirection(option?.defaultDirection ?? 'ASC');
    }
  };

  const handleDirectionChange = (field: string, direction: SortDirection) => {
    setSelectedField(field);
    setSelectedDirection(direction);
  };

  const handleApply = () => {
    dismiss();
    onApply?.({ field: selectedField, direction: selectedDirection });
  };

  const handleClear = () => {
    const defaultOption = options[0];
    dismiss();
    onApply?.({ field: defaultOption.field, direction: defaultOption.defaultDirection });
  };

  const activeOption = options.find((o) => o.field === activeSort.field);
  const activeLabels = activeOption
    ? getDirectionLabels(activeOption.type, t)
    : { asc: '', desc: '' };
  const activeDirectionLabel =
    activeSort.direction === 'ASC' ? activeLabels.asc : activeLabels.desc;
  const currentSortLabel = activeOption ? `${activeOption.label} : ${activeDirectionLabel}` : '';

  return (
    <Popover
      classNames={{ root: 'sort-dropdown' }}
      open={visible}
      content={
        <SortPopoverContent
          popoverTitle={modalLabel ?? t('common:sorting.title')}
          currentSortLabel={currentSortLabel}
          options={options}
          selectedField={selectedField}
          selectedDirection={selectedDirection}
          onFieldChange={handleFieldChange}
          onDirectionChange={handleDirectionChange}
          onClose={dismiss}
          onClear={handleClear}
          onApply={handleApply}
          focus={popupFocus}
        />
      }
      trigger={['click']}
      placement="bottomLeft"
      arrow={false}
      onOpenChange={handleOpenChange}
    >
      {children}
    </Popover>
  );
};

export default SortPopover;
